import { FiMoreVertical as _FiMoreVertical } from "@react-icons/all-files/fi/FiMoreVertical.js";
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, ConfirmDialog, Flex, Menu, MenuItem, Popover, Text } from 'core/components';
import { TbX, TbTrash, TbArrowBarToDown } from 'react-icons/tb';
import Markdown from 'app/views/core/journeys/Markdown';
function formatRelativeTime(timestamp) {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) {
    return 'Just now';
  }
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    return `${diffHour}h ago`;
  }
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay === 1) {
    return 'Yesterday';
  }
  if (diffDay < 7) {
    return `${diffDay}d ago`;
  }
  return new Date(timestamp).toLocaleDateString();
}
const HistoryContainer = styled(Flex).withConfig({
  displayName: "UdeQueryHistoryList__HistoryContainer",
  componentId: "sc-1mdiji1-0"
})(["&&{flex-direction:column;flex:1;min-height:0;}"]);
const Header = styled(Flex).withConfig({
  displayName: "UdeQueryHistoryList__Header",
  componentId: "sc-1mdiji1-1"
})(["&&{align-items:center;justify-content:space-between;padding:12px;border-bottom:", ";}"], ({
  theme
}) => `${theme.borders.thin}`);
const ScrollArea = styled(Box).withConfig({
  displayName: "UdeQueryHistoryList__ScrollArea",
  componentId: "sc-1mdiji1-2"
})(["&&{flex:1;overflow-y:auto;padding:8px 12px;}"]);
const EntryCard = styled(Flex).withConfig({
  displayName: "UdeQueryHistoryList__EntryCard",
  componentId: "sc-1mdiji1-3"
})(["&&{flex-direction:column;padding:8px 10px;margin-bottom:6px;border-radius:4px;cursor:pointer;border-left:3px solid ", ";background:", ";code{font-size:11px;}p{font-size:12px;line-height:20px;}&:hover{background:", ";}}"], ({
  $isActive,
  theme
}) => $isActive ? theme.colors.primary : 'transparent', ({
  $isActive,
  theme
}) => $isActive ? theme.colors.subnavBackground : 'transparent', ({
  theme
}) => theme.colors.subnavBackground);
const Timestamp = styled(Text).withConfig({
  displayName: "UdeQueryHistoryList__Timestamp",
  componentId: "sc-1mdiji1-4"
})(["&&{font-size:11px;color:", ";}"], ({
  theme
}) => theme.colors.muted);
const PromptLabel = styled(Text).withConfig({
  displayName: "UdeQueryHistoryList__PromptLabel",
  componentId: "sc-1mdiji1-5"
})(["&&{font-size:11px;color:", ";font-style:italic;margin-top:2px;}"], ({
  theme
}) => theme.colors.muted);
const OverflowButton = styled(Button).withConfig({
  displayName: "UdeQueryHistoryList__OverflowButton",
  componentId: "sc-1mdiji1-6"
})(["&&{opacity:0.4;&:hover{opacity:1;}}"]);
function UdeQueryHistoryList({
  entries,
  headIndex,
  onSelectEntry,
  onRemoveEntry,
  onSetToHead,
  onClose,
  hideHeader = false
}) {
  const [confirmAction, setConfirmAction] = useState(null);
  const handleConfirm = useCallback(() => {
    if (!confirmAction) {
      return;
    }
    if (confirmAction.type === 'remove') {
      onRemoveEntry(confirmAction.index);
    } else {
      onSetToHead(confirmAction.index);
    }
    setConfirmAction(null);
  }, [confirmAction, onRemoveEntry, onSetToHead]);
  const handleCancel = useCallback(() => {
    setConfirmAction(null);
  }, []);
  if (entries.length === 0) {
    return /*#__PURE__*/React.createElement(HistoryContainer, null, !hideHeader && /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(Text, {
      fontWeight: "bold",
      fontSize: 13
    }, "Query History"), /*#__PURE__*/React.createElement(Button, {
      small: true,
      minimal: true,
      icon: TbX,
      onClick: onClose
    })), /*#__PURE__*/React.createElement(Flex, {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      p: "24px"
    }, /*#__PURE__*/React.createElement(Text, {
      color: "muted",
      fontSize: 12,
      textAlign: "center"
    }, "No history yet. Run a query to start tracking changes.")));
  }
  return /*#__PURE__*/React.createElement(HistoryContainer, null, !hideHeader && /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(Text, {
    fontWeight: "bold",
    fontSize: 13
  }, "Query History"), /*#__PURE__*/React.createElement(Button, {
    small: true,
    minimal: true,
    icon: "cross",
    onClick: onClose
  })), /*#__PURE__*/React.createElement(ScrollArea, null, entries.map((entry, index) => /*#__PURE__*/React.createElement(EntryCard, {
    key: entry.timestamp,
    $isActive: index === headIndex,
    onClick: () => onSelectEntry(index)
  }, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    alignItems: "flex-start"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    minWidth: 0
  }, /*#__PURE__*/React.createElement(Markdown, null, entry.summary), /*#__PURE__*/React.createElement(Timestamp, null, formatRelativeTime(entry.timestamp)), entry.user_prompt && /*#__PURE__*/React.createElement(PromptLabel, null, "\u201C", entry.user_prompt, "\u201D")), /*#__PURE__*/React.createElement("span", {
    onClick: e => e.stopPropagation(),
    role: "presentation"
  }, /*#__PURE__*/React.createElement(Popover, {
    position: "bottom-right"
  }, /*#__PURE__*/React.createElement(OverflowButton, {
    icon: _FiMoreVertical,
    minimal: true,
    small: true
  }), /*#__PURE__*/React.createElement(Menu, {
    minWidth: 160
  }, /*#__PURE__*/React.createElement(MenuItem, {
    icon: TbTrash,
    text: "Remove",
    intent: "danger",
    disabled: index === headIndex,
    onClick: () => setConfirmAction({
      type: 'remove',
      index
    })
  }), /*#__PURE__*/React.createElement(MenuItem, {
    icon: TbArrowBarToDown,
    text: "Set to Head",
    disabled: index === 0,
    onClick: () => setConfirmAction({
      type: 'setToHead',
      index
    })
  })))))))), /*#__PURE__*/React.createElement(ConfirmDialog, {
    isOpen: !!confirmAction,
    title: confirmAction?.type === 'remove' ? 'Remove History Entry?' : 'Set to Head?',
    confirmButtonText: confirmAction?.type === 'remove' ? 'Remove' : 'Set to Head',
    confirmButtonIntent: confirmAction?.type === 'remove' ? 'danger' : 'primary',
    text: confirmAction?.type === 'remove' ? 'This history entry will be permanently removed.' : 'All history entries newer than this one will be permanently removed, and this entry will become the current query.',
    onConfirm: handleConfirm,
    onCancel: handleCancel
  }));
}
export default UdeQueryHistoryList;